/*
Purpose: Single order view with provider tabs, job cards, inline results, and details/overlay panes.
Props:
- orderId: string (required)
- focusJobId?: string
- onClose: () => void (optional, for internal close buttons)

Internal: Subscribes to jobs and results for single order view.
Note: Uses existing CSS classes for consistent styling.
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaClock, FaExclamationTriangle, FaSpinner, FaTimes, FaArrowLeft, FaCompressArrowsAlt, FaExpandArrowsAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { SiAmazonwebservices, SiGoogle, SiOpenai } from 'react-icons/si';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { resolveEvidenceMedia } from '../../../services/mediaResolver';
import { ProviderTabsSkeleton, JobsListSkeleton } from '../../common/SkeletonLoaders';
import { db } from '../../../firebase';
import { playerBus } from '../../../lib/playerBus';
import JobInlineResults from '../../results/display/panels/JobInlineResults';
import ModernResultsPanel from '../../results/display/panels/ModernResultsPanel';
import VehicleDetailsPanel from '../../results/display/panels/VehicleDetailsPanel';
import navigationService from '../../../services/NavigationService';
import { validateAndNormalizeJob } from '../../../utils/jobValidation';
import ReportCardContainer from '../../results/display/reportCards/ReportCardContainer';
import SimilarJobsPanel from '../../results/display/panels/SimilarJobsPanel';
import ModalPortal from '../core/ModalPortal';
import JobCard from '../../results/display/cards/JobCard';
import { useNotifications } from '../../notifications/NotificationsProvider';
import { getVehicles, getPeople, getMasterTimeline, isV4Data, getAnalysisSummary } from '../../../utils/v4DataHelpers';
import { useJobCardCollapse } from '../../../hooks/useJobCardCollapse';
import CollapsibleVideoPlayer from '../../results/display/shared/CollapsibleVideoPlayer';
import PurchaseAccessModal from '../purchase/PurchaseAccessModal';

const providerIcons = {
  vertex_ai_vision: { icon: SiGoogle, color: '#4285f4', name: 'Google Vertex AI' },
  google_video_intelligence: { icon: SiGoogle, color: '#0F9D58', name: 'Google Video Intelligence' },
  openai: { icon: SiOpenai, color: '#00a67e', name: 'OpenAI' },
  aws_bedrock: { icon: SiAmazonwebservices, color: '#ff9900', name: 'AWS Bedrock' }
};

const SingleOrderResults = ({ orderId, focusJobId = null, onActiveTabChange = null, onNavigateToOrder = null, currentUser = null, onUpdateFilters = null, onClose = null, onRequestEvidenceModal = null }) => {
  const { notify } = useNotifications();
  
  // Only log in development and limit logging
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 SingleOrderResults rendered with:', { 
      orderId: orderId?.slice(-8), 
      focusJobId: focusJobId?.slice(-8),
      hasNavigateCallback: !!onNavigateToOrder
    });
  }
  // eslint-disable-next-line no-unused-vars
  const [order, setOrder] = useState(null); // Will be used for order metadata display
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultsByJobId, setResultsByJobId] = useState({});
  const [expandedInlineResults, setExpandedInlineResults] = useState({});
  const [inlineTabByJobId, setInlineTabByJobId] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [layoutMode, setLayoutMode] = useState('default'); // 'default' | 'sidebar' - Reserved for future layout modes
  const resultsUnsubsRef = useRef({});

  // Full details pane
  const [fullDetailsJobId, setFullDetailsJobId] = useState(null);
  const [fullDetailsData, setFullDetailsData] = useState(null);
  const [fullDetailsLoading, setFullDetailsLoading] = useState(false);
  const [fullDetailsJob, setFullDetailsJob] = useState(null); // Store the job data
  const [, setFullDetailsSelectedObjIdx] = useState(null);
  const [fullDetailsEvidenceUrl, setFullDetailsEvidenceUrl] = useState(null);
  
  // Navigation history for similar jobs
  const [originalJob, setOriginalJob] = useState(null);
  const [isViewingSimilarJob, setIsViewingSimilarJob] = useState(false);
  
  // Purchase/Access modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseModalData, setPurchaseModalData] = useState(null);
  
  // Handle privacy toggle for jobs
  const handleJobPrivacyToggle = async (jobId, isPublic) => {
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const jobRef = doc(db, 'jobs', jobId);
      await updateDoc(jobRef, { isPublic });
      
      // Update local state
      setJobs(prevJobs => 
        prevJobs.map(j => j.id === jobId ? { ...j, isPublic } : j)
      );
      
      console.log(`Job ${jobId} privacy updated to: ${isPublic ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error updating job privacy:', error);
      notify({ severity: 'error', title: 'Privacy Update Failed', message: 'Please try again.' });
    }
  };

  // Shared video player state (for all jobs in this order)
  const [sharedVideoUrl, setSharedVideoUrl] = useState(null);
  const [sharedVideoLoading, setSharedVideoLoading] = useState(false);
  const [sharedVideoError, setSharedVideoError] = useState(null);
  const [evidenceName, setEvidenceName] = useState('Evidence');
  const sharedVideoRef = useRef(null);

  // Load order & subscribe to jobs
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    let cleanup = null;
    // eslint-disable-next-line no-unused-vars
    let cancelled = false; // Will be used for cleanup check
    (async () => {
      try {
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        if (orderDoc.exists()) setOrder({ id: orderDoc.id, ...orderDoc.data() });
        else throw new Error('Order not found');

        const jobsQ = query(collection(db, 'jobs'), where('orderId', '==', orderId));
        const unsub = onSnapshot(jobsQ, (snapshot) => {
          const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          console.log(`📋 Jobs loaded for order ${orderId}:`, {
            count: list.length,
            jobIds: list.map(j => j.id),
            focusJobId,
            jobs: list.map(j => ({
              id: j.id,
              status: j.status,
              provider: j.provider,
              hasAnalysisText: !!j.analysisText,
              hasAnalysis: !!j.analysis,
              hasResults: !!j.results,
              actualCost: j.actualCost,
              completedAt: j.completedAt
            }))
          });
          setJobs(list);
          if (focusJobId) {
            const focusJob = list.find(j => j.id === focusJobId);
            if (focusJob) {
              const isOwner = currentUser && (currentUser.uid === focusJob.userId || currentUser.uid === focusJob.createdBy);
              const isPublic = !!focusJob.isPublic;
              // Only auto-expand details if allowed (owner or public)
              setExpandedInlineResults(prev => ({ ...prev, [focusJobId]: !!(isOwner || isPublic) }));
            }
          }
          if (!activeTab && list.length > 0) setActiveTab(list[0].provider);
          setLoading(false);
        }, (err) => { setError('Failed to load analysis results'); setLoading(false); });
        cleanup = () => { unsub(); Object.values(resultsUnsubsRef.current).forEach(fn => { try { fn(); } catch(_) {} }); resultsUnsubsRef.current = {}; };
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    })();
    return () => { if (typeof cleanup === 'function') cleanup(); };
  }, [orderId, focusJobId, activeTab, currentUser]);

  // Subscribe to results for each job and collate structured/summary/targets
  useEffect(() => {
    const unsubs = [];
    jobs.forEach(job => {
      if (resultsUnsubsRef.current[job.id]) return; // already listening
      const resQ = query(collection(db, 'analysisResults'), where('jobId', '==', job.id));
      const unsub = onSnapshot(resQ, (snap) => {
        console.log(`📊 Analysis results query for job ${job.id}:`, {
          empty: snap.empty,
          size: snap.size,
          docs: snap.docs.map(d => d.id)
        });
        
        if (!snap.empty) {
          const first = snap.docs[0].data();
          console.log(`📦 Analysis result data for job ${job.id}:`, {
            hasStructuredData: !!first?.structuredData,
            structuredDataKeys: first?.structuredData ? Object.keys(first.structuredData) : [],
            hasSummary: !!first?.summary,
            hasRawOutput: !!first?.rawOutput
          });
          
          setResultsByJobId(prev => ({
            ...prev,
            [job.id]: {
              promptHash: first?.provenance?.prompt_hash || null,
              modelName: first?.provenance?.model_name || null,
              serviceId: first?.serviceId || null,
              structuredData: first?.structuredData || null,
              summary: first?.structuredData?.summary || first?.summary || null,
              targets: first?.structuredData?.targets || first?.targets || job.selectedTargets || [],
            }
          }));
        } else {
          console.log(`⚠️ No analysis results found for job ${job.id}`);
        }
      });
      resultsUnsubsRef.current[job.id] = unsub;
      unsubs.push(unsub);
    });
    return () => { unsubs.forEach(u => { try { u(); } catch(_) {} }); };
  }, [jobs]);

  // Load shared video URL once for all jobs in this order
  useEffect(() => {
    const loadSharedVideoUrl = async () => {
      // Wait for jobs to load
      if (!jobs || jobs.length === 0) {
        // Reset state only when no jobs
        setSharedVideoUrl(null);
        setSharedVideoError(null);
        setSharedVideoLoading(false);
        return;
      }

      // All jobs in an order should share the same evidence
      // Get evidence info from the first job
      const firstJob = jobs[0];
      if (!firstJob.evidenceId || !firstJob.casefileId) {
        console.log('⚠️ No evidence info in first job');
        setSharedVideoUrl(null);
        setSharedVideoError(null);
        setSharedVideoLoading(false);
        return;
      }

      setSharedVideoLoading(true);
      setSharedVideoError(null);
      
      try {
        console.log('🎬 Loading shared video for evidence:', firstJob.evidenceId);
        
        // Fetch evidence document to get fileName
        try {
          const evidenceDoc = await getDoc(doc(db, 'casefiles', firstJob.casefileId, 'evidence', firstJob.evidenceId));
          if (evidenceDoc.exists()) {
            const evidenceData = evidenceDoc.data();
            setEvidenceName(evidenceData.fileName || 'Evidence');
            console.log('📝 Evidence name loaded:', evidenceData.fileName);
          }
        } catch (err) {
          console.log('⚠️ Could not load evidence name:', err.message);
        }
        
        const media = await resolveEvidenceMedia({
          evidenceId: firstJob.evidenceId,
          casefileId: firstJob.casefileId
        });
        
        if (media?.url) {
          setSharedVideoUrl(media.url);
          console.log('✅ Shared video loaded:', media.url.substring(0, 50) + '...');
        } else {
          console.log('⚠️ No video URL found in media');
        }
      } catch (err) {
        console.error('❌ Failed to load shared video:', err);
        setSharedVideoError('Failed to load video');
      } finally {
        setSharedVideoLoading(false);
      }
    };

    loadSharedVideoUrl();
  }, [jobs]);

  // Connect playerBus to shared video for timeline seeking
  useEffect(() => {
    if (!sharedVideoRef.current || !sharedVideoUrl) {
      return;
    }

    console.log('🔗 Connecting playerBus to shared video');
    
    const unsubscribe = playerBus.onSeek((timestamp) => {
      console.log('⏭️ PlayerBus seek request:', timestamp, 'seconds');
      if (sharedVideoRef.current) {
        sharedVideoRef.current.seekTo(timestamp);
        // Auto-play after seeking for better UX
        sharedVideoRef.current.play();
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log('🔌 Disconnected playerBus from shared video');
      }
    };
  }, [sharedVideoUrl]);

  const jobsByProvider = useMemo(() => {
    const grouped = {};
    jobs.forEach(j => { if (!grouped[j.provider]) grouped[j.provider] = []; grouped[j.provider].push(j); });
    return grouped;
  }, [jobs]);

  // Get current provider jobs for collapse management
  const currentProviderJobs = useMemo(() => {
    return jobsByProvider[activeTab] || [];
  }, [jobsByProvider, activeTab]);

  // Use the collapse hook for the current provider's jobs
  const {
    handleCollapseChange,
    areAllCollapsed,
    toggleAllCollapse,
    isJobCollapsed
  } = useJobCardCollapse(currentProviderJobs);

  const openJobDetails = useCallback(async (job, isFromSimilarJobs = false) => {
    try {
      console.log('📂 Opening job details for:', job);
      console.log('📂 Job keys:', Object.keys(job));
      console.log('📂 Job orderId:', job.orderId);
      console.log('📂 Is from similar jobs:', isFromSimilarJobs);
      
      // ===== SECURITY CHECK: Privacy and Access Control =====
      const isOwner = currentUser && (currentUser.uid === job.userId || currentUser.uid === job.createdBy);
      const isPublic = job?.isPublic || false;
      const hasPurchasedAccess = false; // TODO: Check Firestore purchases collection
      
      // If user doesn't have access, show purchase modal instead
      if (!isOwner && !isPublic && !hasPurchasedAccess) {
        console.log('🔒 User does not have access - showing purchase modal');
        
        // Fetch detection counts for teaser
        const resQ = query(collection(db, 'analysisResults'), where('jobId', '==', job.id));
        const resSnap = await getDocs(resQ);
        let detectionCounts = { vehicles: 0, people: 0, activities: 0, text: 0, timeline: 0 };
        
        if (!resSnap.empty) {
          const data = resSnap.docs[0].data();
          const structured = data.structuredData || {};
          
          // Extract counts from V4 data
          const vehicles = getVehicles(structured);
          const people = getPeople(structured);
          const timeline = getMasterTimeline(structured);
          
          // V4: activities and text are nested in targets, not standalone
          const allTargets = structured.targets || [];
          const activitiesCount = allTargets.reduce((sum, t) => 
            sum + (t.actions?.activities?.length || 0), 0);
          const textCount = 0; // V4 doesn't have standalone text items
          
          detectionCounts = {
            vehicles: vehicles.length,
            people: people.length,
            activities: activitiesCount,
            text: textCount,
            timeline: timeline.length
          };
        }
        
        setPurchaseModalData({
          job,
          detectionCounts
        });
        setShowPurchaseModal(true);
        return; // Don't open full details
      }
      // ===== END SECURITY CHECK =====
      
      // Track navigation history for similar jobs
      if (isFromSimilarJobs && !isViewingSimilarJob) {
        // Store the current job as the original before navigating to similar job
        setOriginalJob({
          id: fullDetailsJobId,
          job: fullDetailsJob,
          data: fullDetailsData,
          evidenceUrl: fullDetailsEvidenceUrl
        });
        setIsViewingSimilarJob(true);
        console.log('📂 Stored original job for back navigation');
      } else if (!isFromSimilarJobs) {
        // Reset navigation history when opening a fresh job (not from similar jobs)
        setOriginalJob(null);
        setIsViewingSimilarJob(false);
      }
      
      // Reset all details state
      setFullDetailsJobId(job.id);
      setFullDetailsJob(job); // Store the job data
      setFullDetailsData(null); // Clear any previous data including selectedVehicle
      setFullDetailsLoading(true);
      setFullDetailsEvidenceUrl(null);
      setFullDetailsSelectedObjIdx(null);
      
      console.log('📂 State reset - opening details for job:', job.id);
      
      // Fetch analysis results
      const resQ = query(collection(db, 'analysisResults'), where('jobId', '==', job.id));
      const resSnap = await getDocs(resQ);
      if (!resSnap.empty) {
        const first = resSnap.docs[0].data();
        setFullDetailsData({ 
          structuredData: first.structuredData || null, 
          summary: first.summary || null, 
          rawOutput: first.rawOutput || null, 
          provenance: first.provenance || null,
          selectedVehicle: null // Explicitly set to null
        });
        console.log('✅ Full details data loaded for job:', job.id);
      } else {
        setFullDetailsData({ 
          structuredData: null, 
          summary: job.results || job.analysisText || null, 
          rawOutput: null,
          selectedVehicle: null // Explicitly set to null
        });
        console.log('⚠️ No analysis results found, using job data for:', job.id);
      }
      
      // Try to fetch evidence URL
      try {
        const evDoc = await getDoc(doc(db, 'casefiles', job.casefileId, 'evidence', job.evidenceId));
        if (evDoc.exists()) {
          setFullDetailsEvidenceUrl(evDoc.data()?.downloadURL || null);
          console.log('✅ Evidence URL loaded');
        }
      } catch(err) {
        console.log('⚠️ Could not load evidence URL:', err.message);
      }
    } catch (error) {
      console.error('❌ Error opening job details:', error);
      setFullDetailsData(null);
    } finally { 
      setFullDetailsLoading(false);
      console.log('📂 Job details loading complete');
    }
  }, [fullDetailsData, fullDetailsEvidenceUrl, fullDetailsJob, fullDetailsJobId, isViewingSimilarJob, currentUser]);

  const closeFullDetails = useCallback(() => {
    setFullDetailsJobId(null);
    setFullDetailsJob(null);
    setFullDetailsData(null);
    setFullDetailsEvidenceUrl(null);
    setFullDetailsSelectedObjIdx(null);
    // Reset navigation history
    setOriginalJob(null);
    setIsViewingSimilarJob(false);
  }, []);

  // Navigate back to original job
  const goBackToOriginalJob = useCallback(() => {
    if (originalJob) {
      console.log('📂 Navigating back to original job:', originalJob.id);
      setFullDetailsJobId(originalJob.id);
      setFullDetailsJob(originalJob.job);
      setFullDetailsData(originalJob.data);
      setFullDetailsEvidenceUrl(originalJob.evidenceUrl);
      setIsViewingSimilarJob(false);
      setOriginalJob(null);
    }
  }, [originalJob]);

  // V4 data extraction - use new helpers
  const parseVehicles = useCallback((data) => {
    if (!data) return [];
    return getVehicles(data);
  }, []);

  const parsePeople = useCallback((data) => {
    if (!data) return [];
    return getPeople(data);
  }, []);

  const parseActivities = useCallback((data) => {
    if (!data) return [];
    // V4: activities are nested in targets
    const targets = data.targets || [];
    const allActivities = [];
    targets.forEach(target => {
      (target.actions?.activities || []).forEach(activity => {
        allActivities.push({
          id: activity.activity_id || activity.id,
          type: activity.type,
          description: activity.description,
          confidence: activity.confidence
        });
      });
    });
    return allActivities;
  }, []);

  const parseTextItems = useCallback((data) => {
    // V4 doesn't have standalone text items
    // Text is embedded in target attributes or master timeline
    return [];
  }, []);

  const parseTimeline = useCallback((data) => {
    if (!data) return [];
    return getMasterTimeline(data);
  }, []);

  // Reserved for future use - order statistics
  // eslint-disable-next-line no-unused-vars
  const orderStats = useMemo(() => {
    const totalJobs = jobs.length;
    const completedJobs = jobs.filter(j => j.status === 'completed').length;
    const totalCost = jobs.reduce((s,j)=>s+(j.actualCost||0),0);
    return { totalJobs, completedJobs, totalCost, completionRate: totalJobs>0?Math.round((completedJobs/totalJobs)*100):0 };
  }, [jobs]);

  const renderProviderResults = (providerId, providerJobs) => {
    const info = providerIcons[providerId];
    if (!info) return null;
    return (
      <div key={providerId} className="provider-results">
        <div className="jobs-list">
          {providerJobs.map(job => {
            const isExpanded = !!expandedInlineResults[job.id];
            const resMeta = resultsByJobId[job.id] || {};
            console.log(`🔍 Processing job ${job.id} for display:`, {
              hasResMeta: !!resMeta,
              hasStructuredData: !!resMeta.structuredData,
              resMetaKeys: Object.keys(resMeta),
              jobStatus: job.status,
              jobHasAnalysisText: !!job.analysisText,
              jobHasAnalysis: !!job.analysis,
              jobHasResults: !!job.results,
              jobKeys: Object.keys(job).filter(k => !['createdAt', 'updatedAt'].includes(k))
            });
            const structured = resMeta.structuredData || {};
            // Filter out placeholder text
            const analysisText = (job.analysisText && 
                                 !job.analysisText.includes('No analysis results available') && 
                                 job.analysisText !== 'Analysis completed.') ? job.analysisText : null;
            
            // Ensure summary is always a string - handle case where V4 JSON object might be in summary field
            const getSummaryText = (value) => {
              if (!value) return null;
              if (typeof value === 'string') return value;
              // If it's an object (like V4 JSON), extract meaningful text
              if (typeof value === 'object') {
                if (value.version === 4 && Array.isArray(value.targets)) {
                  return `V4 Analysis: ${value.targets.length} target(s) detected. Run new scan with increased token limits for complete analysis.`;
                }
                return JSON.stringify(value).substring(0, 200) + '...';
              }
              return String(value);
            };
            
            const summary = getSummaryText(resMeta.summary) || 
                        getSummaryText(structured?.forensic_notes) || 
                        getSummaryText(structured?.summary) || 
                        (typeof job.results === 'string' ? job.results : null) || 
                        analysisText || job.analysis || 
                        (job.status === 'completed' ? `Analysis completed but results not saved. Job ID: ${job.id}. Please check Cloud Function logs or re-run the analysis.` : 
                         job.status === 'processing' ? 'Analysis in progress...' :
                         job.status === 'failed' ? `Analysis failed: ${job.errorMessage || 'Unknown error'}` :
                         'No results available');
            
            console.log(`📝 Summary for job ${job.id}:`, {
              finalSummary: summary ? (typeof summary === 'string' ? summary.substring(0, 100) + '...' : '[Object - V4 JSON detected]') : 'NO SUMMARY',
              summaryType: typeof summary,
              sources: {
                resMeta_summary: !!resMeta.summary,
                structured_forensic: !!structured?.forensic_notes,
                structured_summary: !!structured?.summary,
                job_results: typeof job.results === 'string',
                job_analysisText: !!job.analysisText,
                job_analysis: !!job.analysis
              },
              actualAnalysisText: job.analysisText ? (typeof job.analysisText === 'string' ? job.analysisText.substring(0, 100) + '...' : '[Object]') : null
            });
            const targets = (resMeta.targets || job.selectedTargets || []).filter(Boolean);
            
            // Enhanced resultsMeta with targets for JobCard
            const enhancedResultsMeta = {
              ...resMeta,
              targets: targets.length > 0 ? targets : null
            };
            

            
            const inlineActiveTab = inlineTabByJobId[job.id] || 'summary';
            return (
              <div key={job.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <JobCard 
                  job={job} 
                  resultsMeta={enhancedResultsMeta} 
                  size="regular" 
                  onOpen={() => openJobDetails(job)}
                  collapsible={true}
                  isCollapsed={isJobCollapsed(job.id)}
                  onCollapseChange={(isCollapsed) => handleCollapseChange(job.id, isCollapsed)}
                  hideDetailsButton={true}
                  currentUser={currentUser}
                />
                {/* Check if V4 data and render appropriate panel */}
                {structured?.version === 4 || (Array.isArray(structured?.targets) && structured?.targets.length > 0) ? (
                  <ModernResultsPanel
                    job={job}
                    analysisData={structured}
                    currentUser={currentUser}
                    onPrivacyToggle={handleJobPrivacyToggle}
                    onOpenDetails={() => openJobDetails(job)}
                  />
                ) : (
                  <JobInlineResults
                    job={job}
                    currentUser={currentUser}
                    onPrivacyToggle={handleJobPrivacyToggle}
                    isExpanded={isExpanded}
                    onToggle={() => setExpandedInlineResults(prev => ({ ...prev, [job.id]: !isExpanded }))}
                    onOpenFull={() => openJobDetails(job)}
                    activeTab={inlineActiveTab}
                    onTabChange={(tabId) => { setInlineTabByJobId(prev => ({ ...prev, [job.id]: tabId })); if (typeof onActiveTabChange === 'function') { try { onActiveTabChange(tabId); } catch(_) {} } }}
                    summary={summary}
                    targets={targets}
                    vehicles={parseVehicles(structured)}
                    plates={[]}
                    people={parsePeople(structured)}
                    activities={parseActivities(structured)}
                    textItems={parseTextItems(structured)}
                    timeline={parseTimeline(structured)}
                    onSeek={(t)=>{ try { playerBus.requestSeek(t); } catch(_) {} }}
                    onOpenVehicleDetails={(veh)=>{ setFullDetailsJobId(job.id); setFullDetailsData(prev=>({...(prev||{}), selectedVehicle: veh })); }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="loading-state">
      <ProviderTabsSkeleton providerCount={3} />
      <JobsListSkeleton count={4} size="regular" />
    </div>
  );
  if (error) return (<div className="error-state"><FaExclamationTriangle/><p>{error}</p></div>);

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <div className="provider-tabs">
        {Object.keys(jobsByProvider).map(pid => {
          const info = providerIcons[pid];
          const isActive = activeTab === pid;
          if (!info) return null;
          return (
            <button key={pid} className={`provider-tab ${isActive ? 'active' : ''}`} onClick={()=>setActiveTab(pid)} style={{ '--provider-color': info.color }}>
              <info.icon className="provider-tab-icon" />
              <span>{info.name}</span>
              <div className="tab-stats"><span className="job-count">{jobsByProvider[pid].length}</span></div>
            </button>
          );
        })}
      </div>
      
      {/* Scrollable container for video and content */}
      <div 
        className="single-order-scrollable-content"
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          padding: '16px 0 0 0'
        }}
      >
        {/* Shared Video Player - All jobs control this one video */}
        {(sharedVideoUrl || sharedVideoLoading) && (
          <div style={{ marginBottom: '8px', padding: '0 24px' }}>
            <CollapsibleVideoPlayer
              ref={sharedVideoRef}
              videoUrl={sharedVideoUrl}
              videoTitle={evidenceName}
              isLoading={sharedVideoLoading}
              error={sharedVideoError}
              defaultCollapsed={false}
              maxHeight="600px"
              fitMode="contain"
              onVideoError={(error) => {
                console.error('Video playback error:', error);
                setSharedVideoError('Failed to load video');
              }}
            />
          </div>
        )}
        
        <div className={`results-content ${layoutMode === 'sidebar' ? 'sidebar-layout' : ''}`}>
        {activeTab && jobsByProvider[activeTab] ? (
          <>
            {layoutMode === 'sidebar' ? (
              <div className="sidebar-container">
                <div className="jobs-sidebar">
                  {/* Sidebar Collapse Controls */}
                  <div className="sidebar-collapse-controls">
                    <button 
                      className="collapse-all-btn"
                      onClick={toggleAllCollapse}
                      title={areAllCollapsed ? "Expand all job cards" : "Collapse all job cards"}
                    >
                      {areAllCollapsed ? (
                        <>
                          <FaExpandArrowsAlt />
                          <span>Expand All</span>
                        </>
                      ) : (
                        <>
                          <FaCompressArrowsAlt />
                          <span>Collapse All</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="sidebar-jobs-list">
                    {jobsByProvider[activeTab].map(job => {
                      const resMeta = resultsByJobId[job.id] || {};
                      const targets = (resMeta.targets || job.selectedTargets || []).filter(Boolean);
                      const enhancedResultsMeta = { ...resMeta, targets: targets.length > 0 ? targets : null };
                      
                      return (
                        <JobCard 
                          key={job.id}
                          job={job} 
                          resultsMeta={enhancedResultsMeta} 
                          size="compact" 
                          onOpen={() => openJobDetails(job)}
                          collapsible={true}
                          isCollapsed={isJobCollapsed(job.id)}
                          onCollapseChange={(isCollapsed) => handleCollapseChange(job.id, isCollapsed)}
                          hideDetailsButton={true}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="main-content">
                  {/* Show inline results for the first completed job or first job */}
                  {(() => {
                    const firstCompletedJob = jobsByProvider[activeTab].find(job => job.status === 'completed');
                    const displayJob = firstCompletedJob || jobsByProvider[activeTab][0];
                    if (!displayJob) return null;
                    
                    const isExpanded = true; // Always expanded in sidebar mode
                    const resMeta = resultsByJobId[displayJob.id] || {};
                    const structured = resMeta.structuredData || {};
                    const analysisText = (displayJob.analysisText && 
                                         !displayJob.analysisText.includes('No analysis results available') && 
                                         displayJob.analysisText !== 'Analysis completed.') ? displayJob.analysisText : null;
                    
                    // Use same helper to ensure summary is always a string
                    const getSummaryText = (value) => {
                      if (!value) return null;
                      if (typeof value === 'string') return value;
                      if (typeof value === 'object') {
                        if (value.version === 4 && Array.isArray(value.targets)) {
                          return `V4 Analysis: ${value.targets.length} target(s) detected. Run new scan with increased token limits for complete analysis.`;
                        }
                        return JSON.stringify(value).substring(0, 200) + '...';
                      }
                      return String(value);
                    };
                    
                    const summary = getSummaryText(resMeta.summary) || 
                                getSummaryText(structured?.forensic_notes) || 
                                getSummaryText(structured?.summary) || 
                                (typeof displayJob.results === 'string' ? displayJob.results : null) || 
                                analysisText || displayJob.analysis || 
                                (displayJob.status === 'completed' ? `Analysis completed but results not saved. Job ID: ${displayJob.id}. Please check Cloud Function logs or re-run the analysis.` : 
                                 displayJob.status === 'processing' ? 'Analysis in progress...' :
                                 displayJob.status === 'failed' ? `Analysis failed: ${displayJob.errorMessage || 'Unknown error'}` :
                                 'No results available');
                    
                    const targets = (resMeta.targets || displayJob.selectedTargets || []).filter(Boolean);
                    const inlineActiveTab = inlineTabByJobId[displayJob.id] || 'summary';
                    
                    return (
                      structured?.version === 4 || (Array.isArray(structured?.targets) && structured?.targets.length > 0) ? (
                        <ModernResultsPanel
                          job={displayJob}
                          analysisData={structured}
                          currentUser={currentUser}
                          onPrivacyToggle={handleJobPrivacyToggle}
                          onOpenDetails={() => openJobDetails(displayJob)}
                        />
                      ) : (
                        <JobInlineResults
                          job={displayJob}
                          currentUser={currentUser}
                          onPrivacyToggle={handleJobPrivacyToggle}
                          isExpanded={isExpanded}
                          onToggle={() => {}} // No toggle in sidebar mode
                          onOpenFull={() => openJobDetails(displayJob)}
                          activeTab={inlineActiveTab}
                          onTabChange={(tabId) => { setInlineTabByJobId(prev => ({ ...prev, [displayJob.id]: tabId })); if (typeof onActiveTabChange === 'function') { try { onActiveTabChange(tabId); } catch(_) {} } }}
                          summary={summary}
                          targets={targets}
                          vehicles={parseVehicles(structured)}
                          plates={[]}
                          people={parsePeople(structured)}
                          activities={parseActivities(structured)}
                          textItems={parseTextItems(structured)}
                          timeline={parseTimeline(structured)}
                          onSeek={(t)=>{ try { playerBus.requestSeek(t); } catch(_) {} }}
                          onOpenVehicleDetails={(veh)=>{ setFullDetailsJobId(displayJob.id); setFullDetailsData(prev=>({...(prev||{}), selectedVehicle: veh })); }}
                        />
                      )
                    );
                  })()}
                </div>
              </div>
            ) : (
              renderProviderResults(activeTab, jobsByProvider[activeTab])
            )}
            
             {/* Similar Jobs Panel - Show for completed jobs */}
             {jobs.some(job => job.status === 'completed' && resultsByJobId[job.id]) && (
               <div style={{ marginTop: '24px' }}>
                 <SimilarJobsPanel
                   currentJobId={jobs.find(job => job.status === 'completed' && resultsByJobId[job.id])?.id}
                   currentResults={resultsByJobId[jobs.find(job => job.status === 'completed' && resultsByJobId[job.id])?.id]}
                   currentUser={currentUser}
                   onJobSelect={openJobDetails}
                   onCloseParentModal={onClose}
                  onOpenEvidence={(evidence) => {
                    console.log('📂 Navigation Reset: Request parent to open Evidence modal');
                    // Pass request up to parent (who will survive modal closing)
                    if (onRequestEvidenceModal) {
                      onRequestEvidenceModal(evidence);
                    }
                    // Close Results modal
                    if (onClose) {
                      onClose();
                    }
                  }}
                   onRefreshResultsToEvidence={(evidenceId) => {
                     // When user clicks "Results" from evidence modal in Similar Jobs
                     console.log('🔄 [SingleOrderResults] Refreshing results to show evidence:', evidenceId);
                     console.log('🔄 [SingleOrderResults] onUpdateFilters available?', !!onUpdateFilters);
                     
                     // Update parent modal's filters to show this evidence's results
                     if (onUpdateFilters) {
                       console.log('🔄 [SingleOrderResults] Calling onUpdateFilters with:', { evidenceIds: [evidenceId] });
                       onUpdateFilters({
                         evidenceIds: [evidenceId]
                       });
                       console.log('✅ [SingleOrderResults] Called onUpdateFilters');
                       
                       notify({
                         severity: 'success',
                         title: 'Results Filtered',
                         message: 'Now showing analysis results for this evidence.'
                       });
                     } else {
                       console.warn('⚠️ [SingleOrderResults] onUpdateFilters not available');
                       notify({
                         severity: 'info',
                         title: 'Results View',
                         message: 'Results for this evidence are now displayed.'
                       });
                     }
                   }}
                  defaultExpanded={false}
                  showStatistics={true}
                  className="main-similar-jobs"
                />
              </div>
            )}
          </>
        ) : (
          <div className="no-results-state"><FaClock/><h3>No Results Yet</h3><p>Analysis jobs are still processing. Results will appear here when complete.</p></div>
        )}
        </div>
      </div>

      {/* Vehicle panel */}
      {fullDetailsJobId && fullDetailsData?.selectedVehicle && (
        <ModalPortal>
          <VehicleDetailsPanel vehicle={fullDetailsData.selectedVehicle} onClose={()=>{ setFullDetailsData(prev=>({ ...(prev||{}), selectedVehicle: null })); }} />
        </ModalPortal>
      )}

      {/* Purchase/Access Modal */}
      {showPurchaseModal && purchaseModalData && (
        <PurchaseAccessModal
          job={purchaseModalData.job}
          detectionCounts={purchaseModalData.detectionCounts}
          currentUser={currentUser}
          onClose={() => {
            setShowPurchaseModal(false);
            setPurchaseModalData(null);
          }}
          onPurchase={async ({ jobId, amount, buyerId }) => {
            // TODO: Implement Stripe payment processing
            console.log('💳 Purchase initiated:', { jobId, amount, buyerId });
            notify({ 
              severity: 'info', 
              title: 'Payment Processing', 
              message: 'Payment system coming soon! This feature is under development.' 
            });
            // After successful payment, would add record to purchases collection
          }}
          onRequestAccess={async ({ jobId, message, requesterId, requesterEmail, requesterName }) => {
            // TODO: Implement access request system (send notification to owner)
            console.log('📧 Access request sent:', { jobId, message, requesterId });
            notify({ 
              severity: 'success', 
              title: 'Request Sent', 
              message: 'Your access request has been sent to the owner.' 
            });
            // Would create record in accessRequests collection
          }}
        />
      )}

      {/* Full Details Modal (Summary / JSON / Raw / Overlay / Debug) */}
      {fullDetailsJobId && !fullDetailsData?.selectedVehicle && (
        <ModalPortal>
          {console.log('🎯 RENDERING FULL DETAILS MODAL for job:', fullDetailsJobId)}
          <div className="order-results-overlay nested-modal open" style={{ zIndex: 20000 }} onClick={(e)=>{ if (e.target===e.currentTarget) closeFullDetails(); }}>
            <div className="order-results-modal open" onClick={(e)=>e.stopPropagation()}>
              <div className="order-results-header">
                {isViewingSimilarJob && (
                  <button 
                    className="back-button" 
                    onMouseDown={(e)=>{e.stopPropagation(); goBackToOriginalJob();}}
                    title="Back to original analysis"
                    style={{
                      position: 'absolute',
                      left: '20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                  >
                    <FaArrowLeft size={12} />
                    Back
                  </button>
                )}
                <div className="header-content">
                  {/* Header content removed - moved to footer */}
                </div>
                <button className="close-button" onMouseDown={(e)=>{e.stopPropagation(); closeFullDetails();}}><FaTimes/></button>
              </div>
              <div className="results-content" style={{ flex: 1, overflow: 'auto', padding: '0', margin: 0, width: '100%', minHeight: 0 }}>
                {fullDetailsLoading ? (
                  <div className="loading-state"><FaSpinner className="spinning"/><p>Loading details…</p></div>
                ) : (
                  <ReportCardContainer
                    structuredData={fullDetailsData?.structuredData}
                    job={{
                      ...fullDetailsJob,
                      rawOutput: fullDetailsData?.rawOutput // Add rawOutput from results
                    }}
                    evidenceUrl={fullDetailsEvidenceUrl}
                    onSimilarJobSelect={openJobDetails}
                  />
                )}
              </div>
              <div className="order-results-footer">
                <div className="footer-info">
                  <span>Analysis Details{isViewingSimilarJob && ' (Similar Job)'}</span>
                  <span style={{ margin: '0 12px', opacity: 0.5 }}>•</span>
                  <span>Job {String(fullDetailsJobId).slice(-8)}</span>
                  <span style={{ margin: '0 12px', opacity: 0.5 }}>•</span>
                  <span>Provenance: {fullDetailsData?.provenance?.model_name || '—'}</span>
                </div>
                <div className="footer-actions">
                  <button 
                    className="btn-secondary" 
                    onMouseDown={(e)=>{
                      e.stopPropagation(); 
                      
                      // Get the job data from the stored fullDetailsJob
                      const targetOrderId = fullDetailsJob?.orderId;
                      const targetJobId = fullDetailsJobId;
                      
                      console.log('🔍 Open Job clicked:', { 
                        fullDetailsJob, 
                        targetOrderId, 
                        targetJobId, 
                        currentOrderId: orderId,
                        hasNavigateCallback: !!onNavigateToOrder,
                        jobsInCurrentOrder: jobs.length,
                        fullDetailsJobOrderId: fullDetailsJob?.orderId,
                        onNavigateToOrderType: typeof onNavigateToOrder
                      });
                      
                      // DEBUG: Show the full details job structure
                      console.log('🔍 fullDetailsJob structure:', {
                        id: fullDetailsJob?.id,
                        orderId: fullDetailsJob?.orderId,
                        jobId: fullDetailsJob?.jobId,
                        keys: fullDetailsJob ? Object.keys(fullDetailsJob) : 'null',
                        fullObject: fullDetailsJob
                      });
                      
                      // Close the details modal first
                      closeFullDetails();
                      
                      // FOUNDATIONAL FIX: Use NavigationService for consistent navigation
                      if (targetOrderId && targetJobId) {
                        console.log('🎯 Using NavigationService to navigate:', { targetOrderId, targetJobId, currentOrderId: orderId });
                        
                        // Validate the job data before navigation (relaxed validation for similar jobs)
                        try {
                          const validatedJob = validateAndNormalizeJob(fullDetailsJob, { 
                            isSimilarityResult: true,
                            requireUserId: false 
                          });
                          console.log('✅ Job data validated for navigation:', validatedJob.jobId);
                          
                          // Use NavigationService for consistent routing
                          const success = navigationService.navigateToJob(targetJobId, targetOrderId, {
                            updateUrl: true,
                            source: 'similar_job_open_button'
                          });
                          
                          if (success) {
                            console.log('✅ Navigation successful');
                          } else {
                            console.error('❌ Navigation failed');
                          }
                          
                        } catch (validationError) {
                          console.error('❌ Job validation failed:', validationError.message);
                          console.error('Invalid job data:', fullDetailsJob);
                        }
                      } else {
                        console.error('❌ Cannot navigate: missing targetOrderId or targetJobId', { 
                          targetOrderId, 
                          targetJobId,
                          fullDetailsJob: !!fullDetailsJob
                        });
                      }
                    }}
                  >
                    Open Job
                  </button>
                  <button className="btn-primary" onMouseDown={(e)=>{e.stopPropagation(); closeFullDetails();}}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default SingleOrderResults;


